import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Intl from '@fx-ui/intl';
import { Button, Check, Message } from '@fx-ui/fine-design';
import { SuiteId, useAuthMfa } from '@fx-ui/platform-sdk';
import EventListener from '@fx-ui/react-event-listener';
import {
    get,
    getApi,
    getEncryptedPassword,
    getErrorMsg,
    getQueryRouter,
    getSiteRouter,
    getUrlParameter,
    isEmpty,
    isMobileUA,
    isNumber,
    localStorageManager,
    post,
    redirect,
    sessionStorageManager,
    setTrackerConfig,
    verifyPhone
} from '~/utils';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { InvitationInfo, LoginType } from '~/interfaces';
import RegisterSwitch from '~envs/components/register-switch';
import {
    API,
    ECode,
    EMPTY_FN,
    LOCAL_STORAGE_MIGRATE,
    PWD_RESET_STATUS,
    SESSION_STORAGE,
    SITE_ROUTER,
    URL_PARAMETER
} from '~/constants';
import AccountForm, { AccountFieldType, AccountFormRef, AccountFormValue } from '../account-form';
import { isPhoneCodeLoginCache } from '../../utils/login';
import OtherLogins from '../../components/other-logins';
import SuiteLoginTip from '../../components/suite-login-tip';
import { handleRedirect } from '../../utils/register';
import { defaultPhone, isLoginInvitationMode } from '../../utils/invite';
import { getBrandInfo } from '~/utils/brand';

interface AccountLoginProps {
    loginType: LoginType;
    setLoginType?: (loginType: LoginType, needCache?: boolean) => void;
    otherLoginItems?: LoginType[];
    prefix?: React.ReactNode;
    showRegisterSwitch?: boolean;
    showForgetPassword?: boolean;
    showAutoLogin?: boolean;
    showOtherLogins?: boolean;
    rememberLogin?: boolean;
    defaultLoginName?: string;
    suiteId?: SuiteId;
    loginParams?: {
        tenant_id?: string;
        // TODO @liaw @kodomo 这个参数是为了兼容老接口参数
        tenantId?: string;
    };
    onComplete?: () => void;
    onRegister?: (invitations?: InvitationInfo[]) => void;
}

// 默认登录锁定时间为60分钟
const DEFAULT_LOCKING_TIME = 60;

const AccountLogin: React.FC<AccountLoginProps> = (props) => {
    const {
        loginType,
        setLoginType = EMPTY_FN,
        otherLoginItems,
        prefix,
        showRegisterSwitch: registerSwitchEnabled = true,
        showForgetPassword = true,
        showOtherLogins,
        showAutoLogin = true,
        rememberLogin,
        defaultLoginName,
        loginParams,
        suiteId,
        onComplete,
        onRegister
    } = props;

    const [errorText, setErrorText] = useState<string>();
    const [isVerifyCodeMode, setIsVerifyCodeMode] = useState<boolean>(() => {
        return !defaultLoginName && (loginType === LoginType.PhoneCode || isPhoneCodeLoginCache());
    });
    const [autoLoginChecked, setAutoLoginChecked] = useState(!!rememberLogin);
    const [defaultValue, setDefaultValue] = useState<AccountFormValue>({
        [AccountFieldType.phone_email]: defaultLoginName
    });
    const accountFormRef = useRef<AccountFormRef>(null);

    const { handleMfaError, renderAuthMfa } = useAuthMfa();

    const isEmailMode = isEmpty(defaultPhone) && !!defaultLoginName;
    const showRegisterSwitch = registerSwitchEnabled && !isLoginInvitationMode;
    const showLoginTypeSwitch = !defaultLoginName || (isLoginInvitationMode && !isEmailMode);

    const accountFormItems = useMemo(() => {
        if (isVerifyCodeMode) {
            return [
                { type: AccountFieldType.phone, disabled: !!defaultLoginName, needCache: true },
                { type: AccountFieldType.phone_code }
            ];
        }
        return [
            { type: AccountFieldType.phone_email, disabled: !!defaultLoginName, needCache: true },
            { type: AccountFieldType.raw_password, autoComplete: true }
        ];
    }, [isVerifyCodeMode]);

    useEffect(() => {
        setAutoLoginChecked(!!rememberLogin);
    }, [rememberLogin]);

    const doLogin = (params: Record<string, any>) => {
        return post(
            getApi(API.authentication.signin),
            {
                ...params,
                is_remember: autoLoginChecked,
                suite_id: isNumber(suiteId) ? suiteId : window.platform_suite_id,
                supports_session_anchor: !onComplete,
                redirect_uri: getUrlParameter(URL_PARAMETER.REDIRECT_URI),
                ...loginParams
            },
            {
                customHandleHttpCodes: [400]
            }
        )
            .then((res) => {
                const resData = get(res, 'data', {});
                // 上报神策登录ID
                window.sensors?.login(res?.data?.user?._id);
                /**
                 * 多域下，登录会话统一由平台服务创建
                 * 通过子产品域挂载的平台服务进行登录时，需通过 anchor_url 重定向到平台域建立会话
                 */
                const anchorUrl = resData?.anchor_url;
                if (anchorUrl) {
                    redirect(anchorUrl);
                    return;
                }
                if (resData.is_register) {
                    setTrackerConfig({
                        username: resData.user.username,
                        corp_id: '',
                        tenant_id: '',
                        user_id: resData.user._id,
                        member_id: ''
                    });
                    return new Promise<void>((resolve) => {
                        Message.add({
                            messageType: 'toast',
                            type: 'success',
                            message: Intl.get('account.signin.password.set.skip.registered'),
                            onClose: () => {
                                const { invitations } = resData;
                                onRegister?.(invitations);
                                resolve();
                            }
                        });
                    });
                } else {
                    if (onComplete) {
                        onComplete();
                    } else {
                        handleRedirect(suiteId);
                    }
                }
            })
            .catch((error) => {
                const err = get(error, 'response.data', {});
                // 如果需要二次验证
                if (
                    [ECode.USER_MFA_REQUIRED, ECode.USER_PHONE_UNBIND_BEFORE_MFA].includes(err.code)
                ) {
                    handleMfaError(error, () => {
                        doLogin(params);
                    });
                } else if (err.code === ECode.USER_IS_CANCELING) {
                    sessionStorageManager.set(
                        SESSION_STORAGE.DELETED_ACCOUNT_TOKEN,
                        get(err, 'meta.token', '')
                    );
                    redirect(getSiteRouter(SITE_ROUTER.ACCOUNT.CANCEL_VIEW));
                } else if (err.code === ECode.USER_LOGIN_FAILED_EXCEEDED) {
                    /**
                     * 登录锁定
                     * @see FXD-53307
                     */
                    setErrorText(
                        Intl.get('error.1013', {
                            time: get(err, 'meta.locking_time', DEFAULT_LOCKING_TIME)
                        })
                    );
                } else if (err.code === ECode.USER_PASSWORD_FORMAT_INVALID) {
                    /**
                     * 密码强度校验不通过时重定向到密码重置页
                     * @see FXD-53307
                     */
                    redirect(
                        getQueryRouter(
                            [
                                {
                                    key: URL_PARAMETER.RESET_STATUS,
                                    value: PWD_RESET_STATUS.INVALID_PASSWORD_FORMAT
                                }
                            ],
                            SITE_ROUTER.AUTHENTICATION.RESET_PASSWORD
                        )
                    );
                } else if (err.code === ECode.USER_PASSWORD_EXPIRED_RESET_REQUIRED) {
                    /**
                     * 密码已过期
                     * @see FXD-53307
                     */
                    redirect(
                        getQueryRouter(
                            [
                                {
                                    key: URL_PARAMETER.RESET_STATUS,
                                    value: PWD_RESET_STATUS.PASSWORD_EXPIRED
                                }
                            ],
                            SITE_ROUTER.AUTHENTICATION.RESET_PASSWORD
                        )
                    );
                } else {
                    setErrorText(getErrorMsg(err) || Intl.get('account.signin.failTip'));
                }
            });
    };

    const handleLogin = () => {
        const formValue = accountFormRef.current?.getValue();
        if (isEmpty(formValue)) {
            return;
        }
        return isVerifyCodeMode
            ? doLogin({
                  username: formValue?.[AccountFieldType.phone],
                  phone_code: formValue?.[AccountFieldType.phone_code]
              })
            : doLogin({
                  username: formValue?.[AccountFieldType.phone_email],
                  password: getEncryptedPassword(formValue?.[AccountFieldType.raw_password] || '')
              });
    };

    const handleLoginTypeSwitch = () => {
        setIsVerifyCodeMode((prev) => !prev);
        setErrorText('');
        const formValue = accountFormRef.current?.getValue(true);
        if (isVerifyCodeMode) {
            setLoginType(LoginType.AccountPassword);
            setDefaultValue({
                phone: '',
                [AccountFieldType.phone_email]: formValue?.[AccountFieldType.phone]
            });
            localStorageManager.set(LOCAL_STORAGE_MIGRATE.SIGN_IN_ACCOUNT, true);
        } else {
            setLoginType(LoginType.PhoneCode);
            const loginName = formValue?.[AccountFieldType.phone_email] || '';
            setDefaultValue({
                [AccountFieldType.phone_email]: '',
                phone: verifyPhone(loginName, true) ? loginName : ''
            });
            localStorageManager.set(LOCAL_STORAGE_MIGRATE.SIGN_IN_ACCOUNT, false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        const code = event.key;
        if (code === 'Enter') {
            handleLogin();
        }
    };

    const isMobile = isMobileUA();

    const renderRegisterSwitch = () => {
        return showRegisterSwitch && <RegisterSwitch className="register-switch" />;
    };

    const isShowOtherLogins = () => {
        if (showOtherLogins) {
            return true;
        }
        return !defaultLoginName;
    };

    return (
        <div
            className={classNames('fx-account-login', {
                'is-mobile': isMobile
            })}
        >
            <div
                className={classNames('login-title', {
                    'hide-register-switch': !showRegisterSwitch
                })}
            >
                {getBrandInfo().loginTitle}
            </div>
            {!isMobile && renderRegisterSwitch()}
            <div className="login-content">
                {errorText && <div className="error-tip">{errorText}</div>}
                {prefix}
                <AccountForm
                    className="account-form"
                    ref={accountFormRef}
                    defaultValue={defaultValue}
                    items={accountFormItems}
                    showErrorMsg={!errorText}
                    onError={setErrorText}
                    onChange={() => {
                        setErrorText('');
                    }}
                />
                <div className="login-options">
                    {showAutoLogin && (
                        <Check
                            className="remember-login"
                            checked={autoLoginChecked}
                            onChange={() => {
                                setAutoLoginChecked((prev) => !prev);
                            }}
                        >
                            {Intl.get('account.signin.auto')}
                        </Check>
                    )}
                    {!isVerifyCodeMode && showForgetPassword && (
                        <Hyperlink
                            className="forget-password"
                            compact
                            underline={false}
                            target="_self"
                            href={getSiteRouter(SITE_ROUTER.AUTHENTICATION.FORGOT_PASSWORD)}
                        >
                            {Intl.get('account.signin.forget.pass')}
                        </Hyperlink>
                    )}
                </div>
                <Button
                    type="primary"
                    className="login-btn"
                    size="large"
                    block
                    loadingWhenClick
                    onClick={handleLogin}
                >
                    {Intl.get('signin')}
                </Button>
                <div className="footer">
                    {showLoginTypeSwitch && (
                        <div className="switch-btn" onClick={handleLoginTypeSwitch}>
                            {isVerifyCodeMode
                                ? Intl.get('account.signin.password')
                                : Intl.get('account.signin.validCode')}
                        </div>
                    )}
                    {isMobile && renderRegisterSwitch()}
                </div>
                {isShowOtherLogins() && (
                    <OtherLogins items={otherLoginItems} onLoginTypeChange={setLoginType} />
                )}
            </div>
            <SuiteLoginTip className="login-tip" />
            <EventListener target={document} onKeyDown={handleKeyDown} />
            {renderAuthMfa()}
        </div>
    );
};
export default AccountLogin;
